/**
 * useSwipeNav — swipe left/right between primary nav destinations.
 *
 * On phones, swiping the body horizontally feels native (Instagram tabs,
 * iOS Settings panes).  This hook listens for a clean horizontal swipe
 * gesture on the document body and navigates to the next / previous
 * destination in the provided `paths` list.
 *
 * Rules
 * ─────
 *   • Touch-only (matchMedia `pointer: coarse`).  Desktop is unaffected.
 *   • Triggers only when the gesture is dominantly HORIZONTAL — i.e. the
 *     |Δx| exceeds both the threshold AND the absolute |Δy|.  This
 *     prevents the page-scroll gesture from being hijacked.
 *   • Bails out if the swipe originates inside a horizontally-scrollable
 *     descendant (charts, sortable tables, carousels).  We check up the
 *     DOM tree from `event.target` for any element with overflow-x:auto
 *     that is actually scrollable (scrollWidth > clientWidth).
 *   • Ignores the gesture entirely if the user is currently touching an
 *     `<input>` / `<textarea>` (text-selection drags would otherwise be
 *     mis-routed to navigation).
 *   • One-shot per touchstart — no momentum, no chained swipes.
 *
 * Usage
 * ─────
 *   useSwipeNav(['/me', '/stores', '/reps', '/top10']);
 *
 * Side-effects only — returns nothing.
 */

import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const SWIPE_THRESHOLD = 60;       // minimum px of horizontal travel
const VERTICAL_TOLERANCE = 40;    // |Δx| must exceed |Δy| + this many px
const MAX_DURATION_MS = 500;      // ignore slow drags (probably scrolls / selections)

function isInsideScroller(el) {
  let node = el;
  while (node && node !== document.body) {
    const style = node.nodeType === 1 && window.getComputedStyle(node);
    if (style) {
      const overflowX = style.overflowX;
      if (overflowX === "auto" || overflowX === "scroll") {
        if (node.scrollWidth > node.clientWidth + 1) {
          return true;
        }
      }
    }
    node = node.parentElement;
  }
  return false;
}

function isInsideEditable(el) {
  let node = el;
  while (node && node !== document.body) {
    if (node.nodeType === 1) {
      const tag = node.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || node.isContentEditable) {
        return true;
      }
      // role="textbox" — rare but possible
      if (node.getAttribute?.("role") === "textbox") return true;
    }
    node = node.parentElement;
  }
  return false;
}

export function useSwipeNav(paths) {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!paths?.length) return;
    const isTouch =
      typeof window !== "undefined" &&
      window.matchMedia?.("(pointer: coarse)").matches;
    if (!isTouch) return;

    let startX = 0;
    let startY = 0;
    let startT = 0;
    let active = false;

    const onStart = (e) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      if (isInsideEditable(t.target) || isInsideScroller(t.target)) {
        active = false;
        return;
      }
      active = true;
      startX = t.clientX;
      startY = t.clientY;
      startT = Date.now();
    };

    const onEnd = (e) => {
      if (!active) return;
      active = false;
      const t = e.changedTouches?.[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      const dt = Date.now() - startT;
      if (dt > MAX_DURATION_MS) return;
      if (Math.abs(dx) < SWIPE_THRESHOLD) return;
      if (Math.abs(dx) - Math.abs(dy) < VERTICAL_TOLERANCE) return;

      const idx = paths.indexOf(location.pathname);
      if (idx === -1) return; // current path isn't in the tab list — don't hijack
      // Swipe LEFT (dx < 0) → next tab.  Swipe RIGHT → previous.
      const next = dx < 0 ? idx + 1 : idx - 1;
      if (next < 0 || next >= paths.length) return;
      try { navigator.vibrate?.(8); } catch { /* noop */ }
      navigate(paths[next]);
    };

    document.body.addEventListener("touchstart", onStart, { passive: true });
    document.body.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.body.removeEventListener("touchstart", onStart);
      document.body.removeEventListener("touchend", onEnd);
    };
  }, [paths, navigate, location.pathname]);
}

export default useSwipeNav;
